<script setup lang="ts">
/* =============================================================
   SIDEBAR
   =============================================================
   Generated entirely from `src/config/nav.config.ts` — this file
   contains no product-specific labels or routes.

   Three states, driven by props:
     · expanded  — icons + labels (desktop default)
     · collapsed — icons only, labels as tooltips (desktop)
     · drawer    — overlay panel (below the mobile breakpoint)
   ============================================================= */

import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Logo from '@/components/Logo.vue'
import SidebarLink from './SidebarLink.vue'
import { appConfig } from '@/config/app.config'
import { HOME_ROUTE } from '@/config/routes'
import { isNavItemVisible, navFooterItems, navGroups } from '@/config/nav.config'
import type { UserRole } from '@/lib/auth/types'

const props = defineProps<{
  role: UserRole | undefined
  collapsed: boolean
  /** Mobile only: the drawer is open. */
  drawerOpen: boolean
  isMobile: boolean
}>()

const emit = defineEmits<{ toggleCollapsed: []; closeDrawer: [] }>()

/** Collapsed is a desktop-only state — the drawer is always full width. */
const isCollapsed = computed(() => props.collapsed && !props.isMobile)

// Hide groups that end up empty for this account type.
const groups = computed(() =>
  navGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => isNavItemVisible(item, props.role)) }))
    .filter((group) => group.items.length > 0),
)

const footerItems = computed(() => navFooterItems.filter((item) => isNavItemVisible(item, props.role)))

const marketingHost = appConfig.urls.marketing.replace('https://', '')
</script>

<template>
  <aside
    class="sidebar"
    :data-collapsed="isCollapsed"
    :data-open="drawerOpen"
    aria-label="Main navigation"
  >
    <div class="header">
      <RouterLink :to="HOME_ROUTE" class="brand" @click="isMobile && emit('closeDrawer')">
        <Logo :mark-only="isCollapsed" />
      </RouterLink>

      <button
        v-if="isMobile"
        type="button"
        class="iconButton"
        aria-label="Close navigation"
        @click="emit('closeDrawer')"
      >
        <Icon name="close" :size="20" />
      </button>
      <button
        v-else
        type="button"
        class="iconButton"
        :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="emit('toggleCollapsed')"
      >
        <Icon name="panelLeft" :size="20" />
      </button>
    </div>

    <nav class="nav">
      <div v-for="(group, index) in groups" :key="group.label ?? `group-${index}`" class="group">
        <p v-if="group.label" class="groupLabel">{{ group.label }}</p>
        <ul class="list">
          <SidebarLink v-for="item in group.items" :key="item.to" :item="item" :collapsed="isCollapsed" />
        </ul>
      </div>
    </nav>

    <div class="footer">
      <ul class="list">
        <SidebarLink v-for="item in footerItems" :key="item.to" :item="item" :collapsed="isCollapsed" />
      </ul>

      <a class="homeLink" :href="appConfig.urls.marketing">
        <span class="linkIcon">
          <Icon name="external" :size="16" />
        </span>
        <span class="linkLabel">{{ marketingHost }}</span>
      </a>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  /* Sticky so it stays put while the content column scrolls. The mobile
     rule at the bottom of this file switches it to a fixed overlay —
     both selectors are `.sidebar`, so source order decides. */
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  width: var(--sidebar-width);
  height: 100dvh;
  background-color: var(--color-surface);
  border-right: var(--border-width) solid var(--color-border);
  transition: width var(--dur-base) var(--ease);
  overflow: hidden;
}

/* Collapsed: re-point the width token; labels fade out. */
.sidebar[data-collapsed='true'] {
  --sidebar-width: var(--sidebar-width-collapsed);
}

.sidebar[data-collapsed='true'] .linkLabel,
.sidebar[data-collapsed='true'] .groupLabel,
.sidebar[data-collapsed='true'] .noticeText {
  display: none;
}

.sidebar[data-collapsed='true'] .homeLink {
  justify-content: center;
  padding-inline: 0;
}

.sidebar[data-collapsed='true'] .group + .group {
  margin-top: var(--space-2xs);
}

.sidebar[data-collapsed='true'] .notice {
  justify-content: center;
}

/* --- header --------------------------------------------------- */

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2xs);
  height: var(--topbar-height);
  padding-inline: var(--space-sm);
  border-bottom: var(--border-width) solid var(--color-border);
  flex: none;
}

.sidebar[data-collapsed='true'] .header {
  flex-direction: column;
  gap: var(--space-2xs);
  height: auto;
  padding-block: var(--space-2xs);
}

.brand {
  display: flex;
  min-width: 0;
  text-decoration: none;
}

.iconButton {
  display: grid;
  place-items: center;
  flex: none;
  width: 2rem;
  height: 2rem;
  color: var(--color-text-faint);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  transition:
    color var(--dur-fast) var(--ease),
    background-color var(--dur-fast) var(--ease);
}

.iconButton:hover {
  color: var(--color-heading);
  background-color: var(--color-surface-sunken);
}

/* --- navigation ------------------------------------------------ */

.nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-sm) var(--space-2xs);
}

.group + .group {
  margin-top: var(--space-sm);
}

.groupLabel {
  padding: 0 var(--space-2xs) var(--space-3xs);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--color-text-faint);
}

.list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* --- footer ---------------------------------------------------- */

.footer {
  flex: none;
  padding: var(--space-2xs);
  border-top: var(--border-width) solid var(--color-border);
}

.notice {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3xs);
  margin: var(--space-2xs) 0;
  padding: var(--space-2xs);
  font-size: var(--text-xs);
  line-height: var(--leading-snug);
  color: var(--color-accent-2);
  background-color: var(--color-accent-2-soft);
  border-radius: var(--radius-sm);
}

.notice svg {
  flex: none;
  margin-top: 0.15em;
}

.homeLink {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);
  height: 2rem;
  padding-inline: var(--space-2xs);
  font-size: var(--text-xs);
  color: var(--color-text-faint);
  text-decoration: none;
  border-radius: var(--radius-sm);
}

.homeLink:hover {
  color: var(--color-accent);
  background-color: var(--color-surface-sunken);
}

.homeLink .linkIcon {
  display: grid;
  place-items: center;
  flex: none;
  width: 1.5rem;
}

.homeLink .linkLabel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* --- mobile drawer --------------------------------------------- */

@media (max-width: 56.25rem) {
  .sidebar {
    position: fixed;
    inset-block: 0;
    inset-inline-start: 0;
    z-index: var(--z-overlay);
    box-shadow: var(--shadow-lg);
    transform: translateX(-100%);
    transition: transform var(--dur-base) var(--ease);
  }

  .sidebar[data-open='true'] {
    transform: translateX(0);
  }
}
</style>
